import { errorMessage } from "@atlas/shared";
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Package, ShoppingCart, Truck } from "lucide-react";
import {
  fetchAdminStoreItems,
  createAdminStoreItem,
  patchAdminStoreItem,
  fetchAdminStoreOrders,
  patchAdminStoreOrder,
  type AdminStoreItem,
  type AdminOrder,
  type AdminOrderStatus,
} from "../../api/admin";
import type { StoreItemKind, StoreItemRarity } from "../../api/store";
import { RARITY_OPTIONS, rarityLabel } from "../../lib/rarity";
import { Button } from "../../components/Button";
import { Badge } from "../../components/Badge";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 32,
  padding: "0 10px",
  background: "var(--surface-sunken)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  color: "var(--text-primary)",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
};

const KINDS: StoreItemKind[] = ["badge", "banner", "title", "map_theme", "award", "physical", "bundle"];
const ORDER_STATUSES: AdminOrderStatus[] = ["pending", "paid", "fulfilled", "cancelled", "refunded"];

function gbp(pence: number) {
  return `£${(pence / 100).toFixed(2)}`;
}

function statusVariant(status: AdminOrderStatus): "neutral" | "gold" | "success" | "danger" {
  switch (status) {
    case "pending":
      return "gold";
    case "paid":
    case "fulfilled":
      return "success";
    case "cancelled":
    case "refunded":
      return "danger";
    default:
      return "neutral";
  }
}

// ─── Section toggle ────────────────────────────────────────────────────────────

function SectionToggle({
  value,
  onChange,
}: {
  value: "items" | "orders";
  onChange: (v: "items" | "orders") => void;
}) {
  const opt = (id: "items" | "orders", label: string, icon: React.ReactNode) => {
    const active = value === id;
    return (
      <button
        key={id}
        onClick={() => onChange(id)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 32,
          padding: "0 14px",
          borderRadius: "var(--radius-pill)",
          border: `1px solid ${active ? "var(--border-gold)" : "var(--border-default)"}`,
          background: active ? "var(--accent-soft)" : "transparent",
          color: active ? "var(--text-accent)" : "var(--text-secondary)",
          fontSize: 13,
          fontWeight: active ? 600 : 500,
          cursor: "pointer",
        }}
      >
        {icon}
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {opt("items", "Items", <Package size={14} />)}
      {opt("orders", "Orders", <ShoppingCart size={14} />)}
    </div>
  );
}

// ─── Create item form ──────────────────────────────────────────────────────────

function CreateItemDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = React.useState("");
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [kind, setKind] = React.useState<StoreItemKind>("badge");
  const [priceWaypoints, setPriceWaypoints] = React.useState("");
  const [pricePence, setPricePence] = React.useState("");
  const [stock, setStock] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [featured, setFeatured] = React.useState(false);
  const [sortOrder, setSortOrder] = React.useState("0");
  const [rarity, setRarity] = React.useState<StoreItemRarity>("common");
  const [metadataJson, setMetadataJson] = React.useState("{}");
  const [metadataError, setMetadataError] = React.useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createAdminStoreItem,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin/store/items"] });
      void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
      onClose();
    },
  });

  function submit() {
    setMetadataError(null);
    let metadata: Record<string, unknown> = {};
    try {
      metadata = metadataJson.trim() ? JSON.parse(metadataJson) : {};
    } catch {
      setMetadataError("Metadata must be valid JSON");
      return;
    }
    mutation.mutate({
      slug: slug.trim(),
      name: name.trim(),
      description: description.trim(),
      kind,
      priceWaypoints: priceWaypoints.trim() ? parseInt(priceWaypoints, 10) : null,
      pricePence: pricePence.trim() ? parseInt(pricePence, 10) : null,
      stock: stock.trim() ? parseInt(stock, 10) : null,
      active,
      featured,
      sortOrder: parseInt(sortOrder, 10) || 0,
      rarity,
      metadata,
    });
  }

  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", display: "block", marginBottom: 6 };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          width: 460,
          maxWidth: "100%",
          maxHeight: "88vh",
          overflow: "auto",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 20 }}>
          New store item
        </h2>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Slug</label>
              <input value={slug} onChange={(e) => setSlug(e.target.value)} style={inputStyle} placeholder="compass-rose" />
            </div>
            <div>
              <label style={label}>Kind</label>
              <select value={kind} onChange={(e) => setKind(e.target.value as StoreItemKind)} style={{ ...inputStyle, cursor: "pointer" }}>
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={label}>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} placeholder="Compass Rose" />
          </div>
          <div>
            <label style={label}>Description</label>
            <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Price (Waypoints)</label>
              <input type="number" value={priceWaypoints} onChange={(e) => setPriceWaypoints(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Price (pence)</label>
              <input type="number" value={pricePence} onChange={(e) => setPricePence(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Stock</label>
              <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} style={inputStyle} placeholder="unlimited" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={label}>Rarity</label>
              <select
                value={rarity}
                onChange={(e) => setRarity(e.target.value as StoreItemRarity)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                {RARITY_OPTIONS.map((r) => (
                  <option key={r} value={r}>{rarityLabel(r)}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={label}>Sort order</label>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)" }}>
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-primary)" }}>
              <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
              Featured
            </label>
          </div>
          <div>
            <label style={label}>Metadata (JSON — assetKey / text+colorToken / heatRamp+traceColor / etc.)</label>
            <textarea
              value={metadataJson}
              onChange={(e) => setMetadataJson(e.target.value)}
              rows={3}
              style={{ ...inputStyle, height: "auto", padding: 8, fontFamily: "var(--font-mono)", resize: "vertical" }}
            />
          </div>
        </div>

        {(metadataError || mutation.isError) && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginTop: 12 }}>
            {metadataError ??
              (errorMessage(mutation.error, "Failed to create item"))}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="secondary"
            disabled={mutation.isPending || !slug.trim() || !name.trim() || !description.trim()}
            onClick={submit}
          >
            {mutation.isPending ? "Creating…" : "Create item"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Item row (inline edit) ────────────────────────────────────────────────────

function ItemRow({ item }: { item: AdminStoreItem }) {
  const queryClient = useQueryClient();
  const [priceWaypoints, setPriceWaypoints] = React.useState(item.priceWaypoints?.toString() ?? "");
  const [pricePence, setPricePence] = React.useState(item.pricePence?.toString() ?? "");
  const [stock, setStock] = React.useState(item.stock?.toString() ?? "");
  const [sortOrder, setSortOrder] = React.useState(item.sortOrder.toString());

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin/store/items"] });
    void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
  };

  const patchMutation = useMutation({
    mutationFn: (data: Parameters<typeof patchAdminStoreItem>[1]) => patchAdminStoreItem(item.id, data),
    onSuccess: invalidate,
  });

  const gridColumns = "minmax(0,1.4fr) 90px 110px 110px 90px 110px 60px 70px 70px";

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        gap: 10,
        alignItems: "center",
        padding: "9px 0",
        borderBottom: "1px solid var(--border-subtle)",
        minWidth: 860,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: "var(--text-primary)", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.name}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>{item.slug}</div>
      </div>
      <span className="eyebrow" style={{ fontSize: 9.5 }}>{item.kind}</span>
      <input
        type="number"
        value={priceWaypoints}
        onChange={(e) => setPriceWaypoints(e.target.value)}
        onBlur={() => patchMutation.mutate({ priceWaypoints: priceWaypoints.trim() ? parseInt(priceWaypoints, 10) : null })}
        placeholder="—"
        style={{ ...inputStyle, height: 28, fontSize: 12 }}
      />
      <input
        type="number"
        value={pricePence}
        onChange={(e) => setPricePence(e.target.value)}
        onBlur={() => patchMutation.mutate({ pricePence: pricePence.trim() ? parseInt(pricePence, 10) : null })}
        placeholder="—"
        style={{ ...inputStyle, height: 28, fontSize: 12 }}
      />
      <input
        type="number"
        value={stock}
        onChange={(e) => setStock(e.target.value)}
        onBlur={() => patchMutation.mutate({ stock: stock.trim() ? parseInt(stock, 10) : null })}
        placeholder="∞"
        style={{ ...inputStyle, height: 28, fontSize: 12 }}
      />
      <select
        value={item.rarity}
        onChange={(e) => patchMutation.mutate({ rarity: e.target.value as StoreItemRarity })}
        style={{ ...inputStyle, height: 28, fontSize: 12, cursor: "pointer" }}
      >
        {RARITY_OPTIONS.map((r) => (
          <option key={r} value={r}>{rarityLabel(r)}</option>
        ))}
      </select>
      <input
        type="number"
        value={sortOrder}
        onChange={(e) => setSortOrder(e.target.value)}
        onBlur={() => patchMutation.mutate({ sortOrder: parseInt(sortOrder, 10) || 0 })}
        style={{ ...inputStyle, height: 28, fontSize: 12 }}
      />
      <label style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={item.active}
          onChange={(e) => patchMutation.mutate({ active: e.target.checked })}
        />
      </label>
      <label style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={item.featured}
          onChange={(e) => patchMutation.mutate({ featured: e.target.checked })}
        />
      </label>
    </div>
  );
}

// ─── Items section ─────────────────────────────────────────────────────────────

function StoreItemsSection() {
  const [showCreate, setShowCreate] = React.useState(false);
  const itemsQuery = useQuery({ queryKey: ["admin/store/items"], queryFn: fetchAdminStoreItems });
  const items = itemsQuery.data?.items ?? [];

  const gridColumns = "minmax(0,1.4fr) 90px 110px 110px 90px 110px 60px 70px 70px";
  const headerCell: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
          New item
        </Button>
      </div>

      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: gridColumns, gap: 10, padding: "10px 0 8px", borderBottom: "1px solid var(--border-subtle)", minWidth: 860 }}>
          <span style={headerCell}>Item</span>
          <span style={headerCell}>Kind</span>
          <span style={headerCell}>Waypoints</span>
          <span style={headerCell}>Price</span>
          <span style={headerCell}>Stock</span>
          <span style={headerCell}>Rarity</span>
          <span style={headerCell}>Sort</span>
          <span style={{ ...headerCell, textAlign: "center" }}>Active</span>
          <span style={{ ...headerCell, textAlign: "center" }}>Featured</span>
        </div>

        {itemsQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} /><Skel height={30} /><Skel height={30} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState icon={<Package size={20} />} title="No store items yet" hint="Create the first one above." style={{ margin: "12px 0" }} />
        ) : (
          items.map((item) => <ItemRow key={item.id} item={item} />)
        )}
      </div>

      <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
        {items.reduce((sum, i) => sum + i.ownedCount, 0).toLocaleString()} total owned copies across{" "}
        {items.length.toLocaleString()} items.
      </p>

      {showCreate && <CreateItemDialog onClose={() => setShowCreate(false)} />}
    </div>
  );
}

// ─── Order card ─────────────────────────────────────────────────────────────────

function ShippingAddressBlock({ address }: { address: unknown }) {
  if (!address || typeof address !== "object") return null;
  const a = address as Record<string, unknown>;
  const lines = [a["line1"], a["line2"], a["city"], a["postal_code"], a["country"]]
    .filter((v): v is string => typeof v === "string" && v.length > 0);
  if (lines.length === 0) return null;
  return (
    <div style={{ fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--text-muted)", marginBottom: 2 }}>
        <Truck size={12} /> Shipping
      </span>
      {lines.join(", ")}
    </div>
  );
}

function OrderCard({ order }: { order: AdminOrder }) {
  const queryClient = useQueryClient();
  const [note, setNote] = React.useState(order.fulfilmentNote ?? "");

  const patchMutation = useMutation({
    mutationFn: (data: Parameters<typeof patchAdminStoreOrder>[1]) => patchAdminStoreOrder(order.id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin/store/orders"] });
      void queryClient.invalidateQueries({ queryKey: ["admin/audit-log"] });
    },
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "14px 16px",
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
            {order.userEmail ?? "Unknown user"}
          </div>
          <div className="tnum" style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            {new Date(order.createdAt).toLocaleDateString()} ·{" "}
            {order.totalPence != null
              ? gbp(order.totalPence)
              : order.totalWaypoints != null
                ? `${order.totalWaypoints.toLocaleString()} Waypoints`
                : "—"}
          </div>
        </div>
        <Badge variant={statusVariant(order.status)}>{order.status}</Badge>
      </div>

      {order.items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {order.items.map((line, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--text-secondary)" }}>
              <span>{line.name}</span>
              <span className="tnum" style={{ color: "var(--text-muted)" }}>×{line.quantity}</span>
            </div>
          ))}
        </div>
      )}

      <ShippingAddressBlock address={order.shippingAddress} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, alignItems: "center", marginTop: 4 }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Fulfilment note"
          style={{ ...inputStyle, height: 30 }}
        />
        <Button
          variant="secondary"
          size="sm"
          disabled={patchMutation.isPending || note === (order.fulfilmentNote ?? "")}
          onClick={() => patchMutation.mutate({ fulfilmentNote: note })}
        >
          Save note
        </Button>
        <select
          value={order.status}
          onChange={(e) => patchMutation.mutate({ status: e.target.value as AdminOrderStatus })}
          disabled={patchMutation.isPending}
          style={{ ...inputStyle, height: 30, width: 120, cursor: "pointer" }}
        >
          {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {patchMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", margin: 0 }}>
          {errorMessage(patchMutation.error, "Update failed")}
        </p>
      )}
    </div>
  );
}

// ─── Orders section ─────────────────────────────────────────────────────────────

const ORDER_FILTERS: { label: string; value: string }[] = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Paid", value: "paid" },
  { label: "Fulfilled", value: "fulfilled" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Refunded", value: "refunded" },
];

function StoreOrdersSection() {
  const [status, setStatus] = React.useState("");
  const ordersQuery = useQuery({
    queryKey: ["admin/store/orders", status],
    queryFn: () => fetchAdminStoreOrders(status || undefined),
  });
  const orders = ordersQuery.data?.orders ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {ORDER_FILTERS.map((f) => {
          const active = status === f.value;
          return (
            <button
              key={f.value}
              onClick={() => setStatus(f.value)}
              style={{
                height: 28,
                padding: "0 12px",
                borderRadius: "var(--radius-pill)",
                border: `1px solid ${active ? "var(--border-gold)" : "var(--border-default)"}`,
                background: active ? "var(--accent-soft)" : "transparent",
                color: active ? "var(--text-accent)" : "var(--text-secondary)",
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {ordersQuery.isLoading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Skel height={120} /><Skel height={120} />
        </div>
      ) : orders.length === 0 ? (
        <EmptyState icon={<ShoppingCart size={20} />} title="No orders" hint="Orders will appear here as they come in." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {orders.map((order) => <OrderCard key={order.id} order={order} />)}
        </div>
      )}
    </div>
  );
}

// ─── AdminStorePage ──────────────────────────────────────────────────────────────

export default function AdminStorePage() {
  const { user } = useAuth();
  const [section, setSection] = React.useState<"items" | "orders">("items");

  if (!user?.isAdmin) return <AccessDenied />;

  return (
    <AdminShell title="Store" action={<SectionToggle value={section} onChange={setSection} />}>
      {section === "items" ? <StoreItemsSection /> : <StoreOrdersSection />}
    </AdminShell>
  );
}
